import { MouseEvent, ReactNode, useMemo, useRef } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useQueryClient } from 'react-query';

import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Progress,
  Skeleton,
  Stack,
  Tag,
  TagLabel,
  TagLabelProps,
  TagProps,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MoreHorizontalIcon } from '@udacity/chakra-uds-icons';
import { BreakpointValue, focusStyles } from '@udacity/chakra-uds-theme';

import { ButtonLink } from '~/components/button-link';
import { ShinyHoverEffect } from '~/components/shiny-hover-effect';
import { trackEvent } from '~/features/analytics/track-event';
import { useLocaleCatalogTitleSummary } from '~/features/catalog/hooks/use-locale-catalog-title-summary';
import { CatalogCardItem } from '~/features/catalog/models/catalog-card-item';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { CatalogSearchItem, CatalogSearchResults } from '~/features/catalog/models/catalog-search-result';
import { CatalogCardThemeOverride, resolveCatalogCardTheme } from '~/features/catalog/utils/get-catalog-card-theme';
import { SubscriptionPausedModal } from '~/features/dashboard/components/subscription-paused-modal';
import { publicEnv } from '~/features/environment/public';
import { useClientExperiment } from '~/features/experiments/hooks/use-client-experiment';
import { useExperiment } from '~/features/experiments/hooks/use-experiment';
import { FavoriteMenuItem } from '~/features/favorites/components/favorite-menu-item';
import { i18n } from '~/features/internationalization/internationalization';
import { Subscription } from '~/features/payment/models/subscription';
import { REVIEW_MINIMUM_THRESHOLD } from '~/features/program/constants';
import {
  DELETE_ENROLLMENT_POLICY_ERROR,
  deleteEnrollment,
  DeleteEnrollmentError,
} from '~/features/program/controllers/delete-enrollment';
import { useCourseLink } from '~/features/program/hooks/use-course-link';
import { useEnrollments } from '~/features/program/hooks/use-enrollments';
import { useEnrollmentsProgress } from '~/features/program/hooks/use-enrollments-progress';
import { EnrollmentsProgress } from '~/features/program/models/enrollments-progress';
import { Enrollment } from '~/features/program/models/program-enrollment';
import { getDifficultyLevel } from '~/features/program/utils/get-difficulty-data';
import { getRawDuration } from '~/features/program/utils/get-raw-duration';
import { useIsConnectLearner } from '~/features/sessions/hooks/use-is-connect-learner';
import { useUser } from '~/features/user/hooks/use-user';
import { WoolfDegreeId } from '~/features/woolf/constants';
import { useWoolfUser } from '~/features/woolf/hooks/use-woolf-user';
import { useI18n } from '~/hooks/use-i18n';

import {
  AcademicDegreeTypeIcon,
  CourseTypeIcon,
  Difficulty1Icon,
  Difficulty2Icon,
  Difficulty3Icon,
  EyeIcon,
  NanodegreeTypeIcon,
  PlayIcon,
  StarIcon,
  StopWatchIcon,
  TagIcon,
} from '../icons';

import { FavoriteButton } from './favorite-button';

type CatalogCardWithCatalogItem = {
  catalogItem: CatalogItem | CatalogCardItem | CatalogSearchItem;
  basicItem?: never;
  width?: string | BreakpointValue<string>;
  minWidth?: string | BreakpointValue<string>;
  maxWidth?: string | BreakpointValue<string>;
  grow?: boolean;
  priority?: boolean;
  imageQuality?: number;
  subscription?: Subscription | null;
  enrollment?: Enrollment;
  enrollmentProgress?: EnrollmentsProgress['programs'][number];
  onClick?: (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => void;
  showDuration?: boolean;
  isLocked?: boolean;
  cardBottomSlot?: ReactNode;
  themeOverride?: CatalogCardThemeOverride;
};

type CatalogCardWithBasicItem = {
  catalogItem?: never;
  basicItem: {
    title: string;
    key: string;
  };
  width?: string | BreakpointValue<string>;
  minWidth?: string | BreakpointValue<string>;
  maxWidth?: string | BreakpointValue<string>;
  priority?: boolean;
  grow?: boolean;
  subscription?: never;
  enrollment?: Enrollment;
  enrollmentProgress?: EnrollmentsProgress['programs'][number];
  onClick?: (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => void;
  showDuration?: boolean;
  isLocked?: boolean;
  cardBottomSlot?: ReactNode;
  themeOverride?: CatalogCardThemeOverride;
};

export function CatalogCard(props: CatalogCardWithCatalogItem | CatalogCardWithBasicItem) {
  const { t } = useI18n();
  const hasCatalogItem = isCatalogCardWithCatalogItem(props);
  const showDuration = props.showDuration ?? true;
  const isLocked = props.isLocked ?? false;
  const cardBottomSlot = props.cardBottomSlot ?? null;
  const { userId, userCatalog, userType } = useUser();
  const { isConnectLearner } = useIsConnectLearner();
  const courseSlug = props.catalogItem?.slug ? props.catalogItem.slug : `--${props.catalogItem?.key}`;
  const courseLink = useCourseLink({ courseSlug });

  const { enrollment: serverSideEnrollment, enrollmentProgress: serverSideEnrollmentProgress } = props;
  const { enrollmentsProgress: fetchedEnrollmentsProgress } = useEnrollmentsProgress();

  const programKey = hasCatalogItem ? props.catalogItem.key : props.basicItem.key;
  const { enrollment: fetchedEnrollment, enrollmentsIsLoading } = useEnrollments(programKey);

  const serverVariant = useExperiment()?.['agentic-ai-ft-2026'];
  const hasServerVariant = serverVariant !== undefined;
  const { variant: clientVariant } = useClientExperiment('agentic-ai-ft-2026', {
    enabled: !hasServerVariant && programKey === 'nd900',
  });
  const freeTrialVariant = serverVariant ?? clientVariant;
  const { catalogTitle, catalogSummary } = useLocaleCatalogTitleSummary({
    catalogItem: props.catalogItem,
    basicItem: props.basicItem,
  });
  const localeRawDuration = props?.catalogItem?.rawDurationDisplay
    ? getRawDuration(props.catalogItem.rawDurationDisplay, t)
    : null;

  /**
   * Is Masters Degree?
   */
  const isMastersDegree = hasCatalogItem && props.catalogItem.semanticType === 'Academic_Degree';
  const isMBA = isMastersDegree && (props.catalogItem?.slug || props.catalogItem?.key) === 'mba-ai-product-management';
  const { data: woolfUser } = useWoolfUser({
    enabled: isMastersDegree,
  });

  const isEnrolledInMastersAI = woolfUser?.degreeEnrollments?.some(
    (e) => e.status === 'PENDING' && e.degree.id === WoolfDegreeId.MastersInAI
  );

  const isEnrolledInMBA = woolfUser?.degreeEnrollments?.some(
    (e) => e.status === 'PENDING' && e.degree.id === WoolfDegreeId.MBA
  );

  const localeDifficultyLevel = hasCatalogItem ? getDifficultyLevel(props.catalogItem.difficultyLevel, t) : null;

  /**
   * Primary School
   */
  const school = hasCatalogItem && 'schools' in props.catalogItem ? props.catalogItem.schools[0] : undefined;
  const enrollment = serverSideEnrollment || fetchedEnrollment;
  const hasEnrollment = !!enrollment;
  const showFreeTrialTag = freeTrialVariant === 'on' && programKey === 'nd900' && !hasEnrollment;
  const cardTheme = useMemo(
    () =>
      resolveCatalogCardTheme({
        schoolUri: school?.uri,
        isMastersDegree,
        isMBA,
        themeOverride: props.themeOverride,
      }),
    [school?.uri, isMastersDegree, isMBA, props.themeOverride]
  );

  const programProgress =
    serverSideEnrollmentProgress ||
    fetchedEnrollmentsProgress?.programs.find(
      (program) => program.key === (hasCatalogItem ? props.catalogItem.key : props.basicItem.key)
    );

  const hasReviews = hasCatalogItem
    ? props.catalogItem.reviewSummary.numberOfReviews >= REVIEW_MINIMUM_THRESHOLD
    : false;

  const { subscription } = props;
  const isConsumerSubscriptionPaused = subscription?.status === 'paused' && enrollment?.state === 'PAUSED';
  const formattedResumeDate = subscription?.resumeAt
    ? new Date(subscription?.resumeAt).toLocaleDateString(i18n.language)
    : '';
  const { isOpen: isPausedModalOpen, onOpen: openPausedModal, onClose: closePausedModal } = useDisclosure();

  const fallbackImage = '/images/program-fallbacks/8.png';
  const enterpriseData = hasCatalogItem && 'enterprise' in props.catalogItem && props.catalogItem.enterprise;

  const notStarted = !programProgress || programProgress.completion === 0;
  const isInProgress = !!programProgress && programProgress.completion > 0 && programProgress.completion < 100;

  const isReadyForGraduation =
    enrollment &&
    enrollment.isReadyForGraduation &&
    !enrollment.isGraduated &&
    'isGraduationEnabled' in enrollment &&
    enrollment.isGraduationEnabled;

  const isGraduated =
    !!enrollment && 'isGraduationEnabled' in enrollment && enrollment.isGraduationEnabled && enrollment.isGraduated;

  const Tags = useMemo(() => {
    const sharedProps: TagProps = {
      borderRadius: '4px',
      fontSize: '0.75rem',
      padding: '4px 8px',
      variant: 'no-stroke',
      border: 'none',
      bgColor: 'rgba(0, 0, 0, 0.56)',
      boxShadow: 'none',
    };

    const tagLabelProps: TagLabelProps = {
      color: 'white',
      fontFamily: 'condensed',
      fontSize: '0.875rem',
      lineHeight: '1',
      letterSpacing: '0.08em',
      fontWeight: '400',
      textTransform: 'uppercase',
      noOfLines: 1,
    };

    if (enrollment) {
      return (
        <Tag {...sharedProps}>
          <TagLabel {...tagLabelProps}>{t('program.enrolled')}</TagLabel>
        </Tag>
      );
    }
    if (
      !enrollment &&
      enterpriseData &&
      enterpriseData.applicationIds.length > 0 &&
      (enterpriseData.enrollable || !enterpriseData.enrollable)
    ) {
      return (
        <Tag {...sharedProps}>
          <TagLabel {...tagLabelProps}>{t('program.applyForAccess')}</TagLabel>
        </Tag>
      );
    }
    if (!enrollment && enterpriseData && enterpriseData.enrollable) {
      return (
        <Tag {...sharedProps}>
          <TagLabel {...tagLabelProps}>{t('program.enrollNow')}</TagLabel>
        </Tag>
      );
    }
    if (!enrollment && enterpriseData && !enterpriseData.enrollable) {
      return (
        <Tag {...sharedProps}>
          <TagLabel {...tagLabelProps}>{t('program.requestAccess')}</TagLabel>
        </Tag>
      );
    }
    return null;
  }, [enrollment, enterpriseData, t]);

  const url = useMemo(() => {
    if (enrollmentsIsLoading) return undefined;
    if (enrollment) return `/enrollment/${enrollment.key}`;
    if (hasCatalogItem) {
      switch (courseLink) {
        case '/course/masters-artificial-intelligence':
          return isEnrolledInMastersAI ? '/learning-plan/masters-in-ai' : '/masters-artificial-intelligence';
        case '/course/mba-ai-product-management':
          return isEnrolledInMBA ? '/learning-plan/mba-in-ai' : '/mba-ai-product-management';
        case '/course/tech-discovery-pack':
          return '/learning-plan-landing/tech-discovery-pack';
        default:
          return courseLink;
      }
    }
    return undefined;
  }, [courseLink, enrollment, enrollmentsIsLoading, hasCatalogItem, isEnrolledInMastersAI, isEnrolledInMBA]);

  const shouldSeeUnenrollOption = useMemo(() => {
    return (
      enrollment && !isConnectLearner && (userType !== 'enterprise' || userCatalog?.catalogUrl?.includes('costco'))
    );
  }, [enrollment, isConnectLearner, userCatalog?.catalogUrl, userType]);

  return (
    <Box
      as='article'
      bg={hasEnrollment ? 'white' : cardTheme.backgroundColor}
      border='1px solid'
      borderColor={cardTheme.borderColor}
      display='flex'
      flex={props.grow ? { base: 'unset', xl: 1 } : 'unset'}
      flexDirection='column'
      flexShrink={0}
      maxWidth={props.maxWidth}
      minWidth={props.minWidth}
      overflow='hidden'
      position='relative'
      role='group'
      transition='all 0.3s ease-in-out'
      width={props.width}
      borderRadius={{
        base: '8px',
        md: '12px',
      }}
      onClick={props.onClick}
    >
      <Flex
        bg={!isMastersDegree ? 'white' : undefined}
        boxShadow={!enrollment ? '0px 0px 32px 0px #0000001F' : undefined}
        flex={1}
        flexDir='column'
        bgGradient={
          isMastersDegree
            ? isMBA
              ? 'linear(180deg, #0A0B0F 0%, #3B050E 100%)'
              : 'linear(180deg, #0A0B0F 0%, #092465 100%)'
            : undefined
        }
        borderRadius={{
          base: '8px',
          md: '12px',
        }}
        height={{
          base: !enrollment ? '210px' : 'auto',
          md: !enrollment ? '302px' : '282px',
        }}
        padding={{
          base: !enrollment ? '6px 6px 12px' : '6px 6px 0px',
          md: !enrollment ? '8px 8px 20px' : '8px 8px 0px',
        }}
      >
        <Flex
          borderRadius={{
            base: '4px',
            md: '8px',
          }}
          {...focusStyles('blue.500')}
          bgColor={cardTheme.accentColor}
          overflow='hidden'
          position='relative'
          flex={{
            base: '0 0 78px',
            md: '0 0 120px',
          }}
        >
          <ShinyHoverEffect />
          <Box overflow='hidden' position='relative' width='100%'>
            <Box inset={0} mixBlendMode={!isMastersDegree ? 'hard-light' : undefined} position='absolute'>
              {hasCatalogItem && props.catalogItem.image && typeof props.catalogItem.image === 'string' && (
                <Image
                  alt=''
                  priority={Boolean(props.priority)}
                  quality={75}
                  sizes='360px'
                  src={props.catalogItem.image}
                  style={{
                    objectFit: 'cover',
                    pointerEvents: 'none',
                    filter: !isMastersDegree ? 'grayscale(100%)' : undefined,
                  }}
                  fill
                />
              )}
              {!props.catalogItem?.image && (
                <Image
                  alt=''
                  priority={Boolean(props.priority)}
                  quality={75}
                  sizes='360px'
                  src={fallbackImage}
                  style={{
                    objectFit: 'cover',
                    pointerEvents: 'none',
                    filter: 'grayscale(100%)',
                  }}
                  fill
                />
              )}
            </Box>

            {!!cardTheme.iconPath && (
              <Box
                bottom='8px'
                left='8px'
                position='absolute'
                height={{
                  base: '28px',
                  md: '40px',
                }}
                width={{
                  base: '28px',
                  md: '40px',
                }}
              >
                <Image
                  alt={school?.name ?? ''}
                  quality={75}
                  src={cardTheme.iconPath}
                  style={{
                    display: 'block',
                    pointerEvents: 'none',
                  }}
                  fill
                />
              </Box>
            )}
            {!!Tags && (
              <Flex left='8px' position='absolute' right='48px' top='8px'>
                {Tags}
              </Flex>
            )}
            {showFreeTrialTag && (
              <Box left='8px' pointerEvents='none' position='absolute' top='8px' zIndex={2}>
                <Tag
                  bgColor='white'
                  border='none'
                  borderRadius='4px'
                  boxShadow='none'
                  padding='4px 8px'
                  variant='no-stroke'
                >
                  <TagLabel
                    color='#00298C'
                    fontFamily='condensed'
                    fontSize='0.875rem'
                    fontWeight='400'
                    letterSpacing='1.12px'
                    lineHeight='1'
                    noOfLines={1}
                    textTransform='uppercase'
                  >
                    Free Trial
                  </TagLabel>
                </Tag>
              </Box>
            )}
          </Box>
        </Flex>

        <Flex
          flex={1}
          flexDir='column'
          gap={{
            base: '10px',
            md: '12px',
          }}
          pt={{
            base: '10px',
            md: 4,
          }}
          px={{
            base: 2,
            md: 3,
          }}
        >
          <Box noOfLines={4}>
            <Heading
              as={isLocked ? 'span' : 'a'}
              color={cardTheme.titleColor}
              display='block'
              fontFamily='body'
              fontWeight='600'
              lineHeight='1.4'
              mb={2}
              fontSize={{
                base: '0.75rem',
                md: '0.9375rem',
              }}
              sx={{
                '&::before': {
                  display: 'block',
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  zIndex: 1,
                },
              }}
              {...(!isLocked && { href: url })}
            >
              {catalogTitle}
            </Heading>

            {!!catalogSummary && (
              <Text
                color={cardTheme.textColor}
                fontSize={{
                  base: '0.625rem',
                  md: '0.8125rem',
                }}
                lineHeight={{
                  base: '1.3',
                  md: '1.4',
                }}
              >
                {catalogSummary}
              </Text>
            )}
          </Box>

          {hasCatalogItem && (
            <Flex
              gap='8px'
              justifyContent='space-between'
              marginTop='auto'
              _empty={{
                display: 'none',
              }}
              display={
                !enrollment
                  ? 'flex'
                  : {
                      base: 'none',
                      md: 'flex',
                    }
              }
            >
              <Flex>
                {'semanticType' in props.catalogItem && (
                  <>
                    {props.catalogItem.semanticType === 'Degree' && (
                      <Flex alignItems='center' gap='8px'>
                        <NanodegreeTypeIcon
                          color={cardTheme.accentColor}
                          height={{
                            base: '10px',
                            md: '14px',
                          }}
                          width={{
                            base: '10px',
                            md: '14px',
                          }}
                        />

                        <Text
                          color={cardTheme.titleColor}
                          fontFamily='condensed'
                          fontWeight='500'
                          letterSpacing='5%'
                          lineHeight='1.4'
                          noOfLines={1}
                          textTransform='uppercase'
                          wordBreak='break-all'
                          fontSize={{
                            base: '0.625rem',
                            md: '0.8125rem',
                          }}
                        >
                          {t('program.semanticType.degree')}
                        </Text>
                      </Flex>
                    )}
                    {['Part', 'Course'].includes(props.catalogItem.semanticType as 'Part' | 'Course') && (
                      <Flex alignItems='center' gap='8px'>
                        <CourseTypeIcon
                          color={cardTheme.accentColor}
                          height={{
                            base: '10px',
                            md: '14px',
                          }}
                          width={{
                            base: '10px',
                            md: '14px',
                          }}
                        />
                        <Text
                          color={cardTheme.titleColor}
                          fontFamily='condensed'
                          fontWeight='500'
                          letterSpacing='5%'
                          noOfLines={1}
                          textTransform='uppercase'
                          wordBreak='break-all'
                          fontSize={{
                            base: '0.625rem',
                            md: '0.8125rem',
                          }}
                          lineHeight={{
                            base: '1',
                            md: '1.4',
                          }}
                        >
                          {t('program.semanticType.course')}
                        </Text>
                      </Flex>
                    )}
                    {props.catalogItem.semanticType === 'Academic_Degree' && (
                      <Flex alignItems='center' gap='8px'>
                        <AcademicDegreeTypeIcon
                          color={cardTheme.accentColor}
                          height={{
                            base: '10px',
                            md: '14px',
                          }}
                        />
                        <Text
                          color={cardTheme.titleColor}
                          fontFamily='condensed'
                          fontWeight='500'
                          letterSpacing='5%'
                          noOfLines={1}
                          textTransform='uppercase'
                          wordBreak='break-all'
                          fontSize={{
                            base: '0.625rem',
                            md: '0.8125rem',
                          }}
                        >
                          {t('program.mastersDegree')}
                        </Text>
                      </Flex>
                    )}
                  </>
                )}
              </Flex>
              {hasReviews && (
                <Flex alignItems='center' gap='4px'>
                  <StarIcon
                    color='orange.500'
                    height={{
                      base: '10px',
                      md: '12px',
                    }}
                    width={{
                      base: '10px',
                      md: '12px',
                    }}
                  />
                  <Text
                    color='orange.700'
                    fontWeight='500'
                    fontSize={{
                      base: '0.625rem',
                      md: '0.8125rem',
                    }}
                    lineHeight={{
                      base: '1',
                      md: '1.4',
                    }}
                  >
                    {props.catalogItem.reviewSummary.starsAverage}
                  </Text>
                </Flex>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>

      {enrollment && (
        <Flex
          flexDir='column'
          padding={{ base: '12px 12px 8px', md: '16px 20px 14px' }}
          position='relative'
          _empty={{
            display: 'none',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Flex borderTop='1px solid' borderTopColor='grey.100' flex='1' gap='8px' marginTop='auto' paddingTop='12px'>
            {!isReadyForGraduation && isConsumerSubscriptionPaused && (
              <Box position='relative' zIndex={2}>
                <SubscriptionPausedModal
                  isOpen={isPausedModalOpen}
                  paymentDate={formattedResumeDate}
                  onClose={closePausedModal}
                />
                <Button
                  colorScheme='gray'
                  leftIcon={<PlayIcon h='16px' w='16px' />}
                  size='sm'
                  onClick={openPausedModal}
                >
                  {t('settings.subscription.resumeSubscription')}
                </Button>
              </Box>
            )}
            {!isReadyForGraduation && !isGraduated && !isConsumerSubscriptionPaused && isInProgress && (
              <Flex
                flex={1}
                alignItems={{
                  md: 'center',
                }}
                flexDirection={{
                  base: 'column-reverse',
                  md: 'row',
                }}
                gap={{
                  base: 2,
                  md: 3,
                }}
              >
                <Box
                  flex={1}
                  position='relative'
                  zIndex={2}
                  flexBasis={{
                    md: '98px',
                  }}
                >
                  <ButtonLink
                    linkProps={{ href: `${enrollment.classroomLink}/resume-learning` }}
                    buttonProps={{
                      'aria-label': t('program.continueProgram', {
                        programName: hasCatalogItem ? props.catalogItem.title : props.basicItem.title,
                      })!,
                      size: {
                        base: 'xs',
                        md: 'sm',
                      },
                      zIndex: 2,
                      width: 'full',
                      leftIcon: <PlayIcon h='16px' w='16px' />,
                      onClick: () => {
                        trackEvent({
                          type: 'click',
                          name: 'catalogCtaClicked',
                          action: 'program_card_clicked',
                          label: enrollment.key,
                          value: 'resume',
                        });
                      },
                    }}
                  >
                    {t('program.resume')}
                  </ButtonLink>
                </Box>
                <Stack
                  alignItems='flex-start'
                  flex={1}
                  spacing='4px'
                  flexBasis={{
                    md: '142px',
                  }}
                >
                  <Progress
                    aria-label={t('program.progressIs', { percent: programProgress?.completion ?? 0 })!}
                    bg='blue.700'
                    data-testid='catalog-card-progress-bar'
                    height='7px'
                    padding='1.5px'
                    value={programProgress?.completion}
                    width='100%'
                    css={{
                      '[role="progressbar"]': {
                        background: 'linear-gradient(90deg, #7BA2FF 0%, #89E6FF 100%)',
                      },
                      pointerEvents: 'none',
                    }}
                  />
                  <Text color='grey.900' fontWeight='500' size='caption'>
                    {t('common.percent', { val: programProgress?.completion })}
                  </Text>
                </Stack>
              </Flex>
            )}
          </Flex>

          {notStarted && (
            <ButtonLink
              buttonProps={{
                colorScheme: 'blue',
                width: {
                  base: 'full',
                  md: 'fit-content',
                },
                mt: '4px',
                size: {
                  base: 'xs',
                  md: 'sm',
                },
                zIndex: 2,
                leftIcon: <PlayIcon h='16px' w='16px' />,
                onClick: () => {
                  trackEvent({
                    type: 'click',
                    name: 'catalogCtaClicked',
                    action: 'program_card_clicked',
                    label: enrollment.key,
                    value: 'start_learning',
                  });
                },
              }}
              linkProps={{
                href: `${enrollment.classroomLink}/resume-learning`,
              }}
            >
              {t('program.startLearning')}
            </ButtonLink>
          )}

          {isReadyForGraduation && (
            <ButtonLink
              buttonProps={{
                colorScheme: 'blue',
                width: 'full',
                mt: '4px',
                size: 'sm',
                zIndex: 2,
              }}
              linkProps={{
                href: `${publicEnv.NEXT_PUBLIC_LEARN_URL}/graduation/${enrollment.key}`,
              }}
            >
              {t('program.graduateNow')}
            </ButtonLink>
          )}

          {isGraduated && (
            <ButtonLink
              buttonProps={{
                colorScheme: 'blue',
                width: {
                  base: 'full',
                  md: 'fit-content',
                },
                mt: '4px',
                size: {
                  base: 'xs',
                  md: 'sm',
                },
                zIndex: 2,
                variant: 'outline',
                leftIcon: <EyeIcon height='14px' width='14px' />,
                onClick: () => {
                  trackEvent({
                    type: 'click',
                    name: 'catalogCtaClicked',
                    action: 'program_card_clicked',
                    label: enrollment.key,
                    value: 'review_content',
                  });
                },
              }}
              linkProps={{
                href: `${enrollment.classroomLink}/resume-learning`,
              }}
            >
              {t('program.reviewContent')}
            </ButtonLink>
          )}
        </Flex>
      )}

      {!enrollment && hasCatalogItem && (localeDifficultyLevel || localeRawDuration || cardBottomSlot) && (
        <Flex
          borderBottomRadius='12px'
          gap={1}
          justifyContent='space-between'
          padding={{ base: '8px 12px', md: '14px 20px' }}
        >
          {!cardBottomSlot && (
            <Flex alignItems='center' color={cardTheme.textColor} gap={1}>
              {localeDifficultyLevel && !isMastersDegree && (
                <>
                  {(props.catalogItem.difficultyLevel === 'Beginner' ||
                    props.catalogItem.difficultyLevel === 'Discovery' ||
                    props.catalogItem.difficultyLevel === 'Fluency') && (
                    <Difficulty1Icon
                      height={{
                        base: '12px',
                        md: '16px',
                      }}
                      width={{
                        base: '12px',
                        md: '16px',
                      }}
                    />
                  )}
                  {props.catalogItem.difficultyLevel === 'Intermediate' && (
                    <Difficulty2Icon
                      height={{
                        base: '12px',
                        md: '16px',
                      }}
                      width={{
                        base: '12px',
                        md: '16px',
                      }}
                    />
                  )}
                  {props.catalogItem.difficultyLevel === 'Advanced' && (
                    <Difficulty3Icon
                      height={{
                        base: '12px',
                        md: '16px',
                      }}
                      width={{
                        base: '12px',
                        md: '16px',
                      }}
                    />
                  )}
                  <Text
                    lineHeight='1.2'
                    noOfLines={1}
                    wordBreak='break-all'
                    fontSize={{
                      base: '0.625rem',
                      md: '0.75rem',
                    }}
                  >
                    {localeDifficultyLevel}
                  </Text>
                </>
              )}
              {isMastersDegree && (
                <>
                  <TagIcon
                    height={{
                      base: '12px',
                      md: '16px',
                    }}
                    width={{
                      base: '12px',
                      md: '16px',
                    }}
                  />
                  <Text
                    lineHeight='1.2'
                    noOfLines={1}
                    wordBreak='break-all'
                    fontSize={{
                      base: '0.625rem',
                      md: '0.75rem',
                    }}
                  >
                    {t('program.affordable')}
                  </Text>
                </>
              )}
            </Flex>
          )}
          {cardBottomSlot}
          <Flex alignItems='center' color={cardTheme.textColor} gap={1}>
            {localeRawDuration && !isMastersDegree && showDuration && (
              <>
                <StopWatchIcon
                  height={{
                    base: '12px',
                    md: '16px',
                  }}
                  width={{
                    base: '12px',
                    md: '16px',
                  }}
                />
                <Text
                  noOfLines={1}
                  textTransform='lowercase'
                  wordBreak='break-all'
                  fontSize={{
                    base: '0.625rem',
                    md: '0.75rem',
                  }}
                  lineHeight={{
                    base: '1.2',
                    md: '0.9',
                  }}
                >
                  {localeRawDuration}
                </Text>
              </>
            )}
            {isMastersDegree && (
              <>
                <StopWatchIcon
                  height={{
                    base: '12px',
                    md: '16px',
                  }}
                  width={{
                    base: '12px',
                    md: '16px',
                  }}
                />
                <Text
                  lineHeight='1.2'
                  noOfLines={1}
                  wordBreak='break-all'
                  fontSize={{
                    base: '0.625rem',
                    md: '0.75rem',
                  }}
                >
                  {t('program.flexible')}
                </Text>
              </>
            )}
          </Flex>
        </Flex>
      )}

      {!!userId && !isLocked && (
        <Box position='absolute' right='16px' top='16px' zIndex={2}>
          {shouldSeeUnenrollOption ? (
            <OptionsMenu
              programKey={hasCatalogItem ? props.catalogItem.key : props.basicItem.key}
              programTitle={hasCatalogItem ? props.catalogItem.title : props.basicItem.title}
            />
          ) : (
            <FavoriteButton
              programKey={hasCatalogItem ? props.catalogItem.key : props.basicItem.key}
              programTitle={hasCatalogItem ? props.catalogItem.title : props.basicItem.title}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

export function CatalogCardSkeleton({
  width = '190px',
  maxWidth = 'unset',
  type = 'catalog',
}: {
  width?: string | BreakpointValue<string>;
  maxWidth?: string | BreakpointValue<string>;
  type?: 'catalog' | 'enrollment';
}) {
  return (
    <Flex
      bg='white'
      border='1px solid'
      borderColor='grey.100'
      flexDirection='column'
      flexShrink={0}
      maxWidth={maxWidth}
      minWidth='152px'
      overflow='hidden'
      width={width}
      borderRadius={{
        base: '8px',
        md: '12px',
      }}
      height={{
        base: 'auto',
        md: '302px',
      }}
    >
      <Flex
        bg='white'
        boxShadow='0px 0px 32px 0px #0000001F'
        flexDir='column'
        borderRadius={{
          base: '8px',
          md: '12px',
        }}
        height={{
          base: '210px',
          md: '302px',
        }}
        padding={{
          base: '6px 6px 12px',
          md: '8px',
        }}
      >
        <Flex
          overflow='hidden'
          position='relative'
          borderRadius={{
            base: '4px',
            md: '8px',
          }}
          flex={{
            base: '0 0 78px',
            md: '0 0 120px',
          }}
        >
          <Skeleton
            height='100%'
            width='100%'
            borderTopLeftRadius={{
              base: '4px',
              md: '8px',
            }}
            borderTopRightRadius={{
              base: '4px',
              md: '8px',
            }}
          />
          <Box
            bottom='10px'
            left='12px'
            position='absolute'
            height={{
              base: '28px',
              md: '40px',
            }}
            width={{
              base: '28px',
              md: '40px',
            }}
          >
            <Skeleton borderRadius='4px' height='100%' width='100%' />
          </Box>
        </Flex>

        <Flex
          flex={1}
          flexDir='column'
          gap={{
            base: '10px',
            md: '10px',
          }}
          pt={{
            base: '10px',
            md: 4,
          }}
          px={{
            base: 2,
            md: 3,
          }}
        >
          <Flex flexDirection='column' gap={2}>
            <Skeleton
              width='80%'
              h={{
                base: '14px',
                md: '15px',
              }}
            />
            <Skeleton
              width='100%'
              h={{
                base: '10px',
                md: '13px',
              }}
            />
            <Skeleton
              width='90%'
              h={{
                base: '10px',
                md: '13px',
              }}
            />
            <Skeleton
              width='75%'
              h={{
                base: '10px',
                md: '13px',
              }}
            />
          </Flex>

          {type === 'catalog' && (
            <>
              <Flex gap='8px' justifyContent='space-between' marginTop='auto'>
                <Flex alignItems='center' flex={1} gap='8px'>
                  <Skeleton
                    h={{
                      base: '10px',
                      md: '14px',
                    }}
                    w={{
                      base: '10px',
                      md: '14px',
                    }}
                  />
                  <Skeleton
                    flex={1}
                    h={{
                      base: '10px',
                      md: '13px',
                    }}
                  />
                </Flex>
                <Flex alignItems='center' gap='4px'>
                  <StarIcon
                    color='grey.200'
                    height={{
                      base: '10px',
                      md: '12px',
                    }}
                    width={{
                      base: '10px',
                      md: '12px',
                    }}
                  />
                  <Skeleton
                    width='24px'
                    h={{
                      base: '10px',
                      md: '13px',
                    }}
                  />
                </Flex>
              </Flex>
            </>
          )}
          {type === 'enrollment' && <Skeleton h='36px' mt='auto' width='100%' />}
        </Flex>
      </Flex>

      {type === 'catalog' && (
        <Flex
          borderBottomRadius='12px'
          gap={1}
          justifyContent='space-between'
          padding={{ base: '8px 12px', md: '14px 20px' }}
        >
          <Flex alignItems='center' flex={1} gap={1}>
            <Skeleton
              h={{
                base: '12px',
                md: '16px',
              }}
              w={{
                base: '12px',
                md: '16px',
              }}
            />
            <Skeleton
              flex={1}
              h={{
                base: '10px',
                md: '12px',
              }}
            />
          </Flex>
          <Flex alignItems='center' flex={1} gap={1}>
            <Skeleton
              flex={1}
              h={{
                base: '12px',
                md: '16px',
              }}
              w={{
                base: '12px',
                md: '16px',
              }}
            />
            <Skeleton
              width='64px'
              h={{
                base: '10px',
                md: '12px',
              }}
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}

export function isCatalogCardWithCatalogItem(
  catalogCardProps: CatalogCardWithCatalogItem | CatalogCardWithBasicItem
): catalogCardProps is CatalogCardWithCatalogItem {
  return catalogCardProps.catalogItem !== undefined;
}

export function OptionsMenu({ programKey, programTitle }: { programKey: string; programTitle: string | null }) {
  const { t } = useI18n();
  const toast = useToast();
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  async function handleRemoveEnrollment() {
    try {
      await deleteEnrollment(programKey);
      // Note: when removing an enrollment, we need to remove the program from cached catalog search results after successful response.
      const cachedEnrolledProgramsQueryData =
        queryClient.getQueriesData('catalog')?.filter(([key]) => {
          const queryKey = key as [string, { scope?: string[] }];
          return queryKey[1]?.scope?.some((scope) => scope === 'enrolled' || scope === 'completed');
        }) || [];
      for (const [key, value] of cachedEnrolledProgramsQueryData as [string, CatalogSearchResults][]) {
        const prevSearchResultItems = value?.searchResultItems;
        if (prevSearchResultItems) {
          const newSearchResultItems = prevSearchResultItems.filter((item) => item.key !== programKey);
          const nextNumberOfHits = value.numberOfHits - 1;
          queryClient.setQueryData(key, {
            ...value,
            searchResultItems: newSearchResultItems,
            numberOfHits: nextNumberOfHits,
            numberOfPages: value.pageSize > 0 ? Math.ceil(nextNumberOfHits / value.pageSize) : 0,
          });
        }
      }

      router.replace(router.asPath);

      toast({
        variant: 'success',
        title: t('program.enrollmentRemoved'),
      });
    } catch (error) {
      if (error instanceof DeleteEnrollmentError && error.name === DELETE_ENROLLMENT_POLICY_ERROR) {
        toast({
          variant: 'error',
          title: t('program.errorRemovingEnrollmentBlockedByPolicy'),
        });
        return;
      }
      toast({
        variant: 'error',
        title: t('program.errorRemovingEnrollment'),
      });
    }
  }

  function handleMenuClose() {
    // when closing the menu it will add focus to the card causing the carousel to slide putting the card in the first position
    // to prevent this we force the focus to go to the next or prev button
    // @ts-ignore
    containerRef.current?.closest('.swiper')?.querySelector('.swiper-button-container button:not([disabled])')?.focus();
  }

  return (
    <Box ref={containerRef} onClick={(e) => e.stopPropagation()}>
      <Menu placement='bottom-end' onClose={handleMenuClose}>
        <MenuButton
          _active={{ bgColor: 'hsla(0, 0%, 100%, 0.4)' }}
          _focusVisible={{ outlineColor: 'white', outlineOffset: '-6px' }}
          _hover={{ bgColor: 'hsla(0, 0%, 100%, 0.4)' }}
          aria-label='Enrollment options'
          as={IconButton}
          bgColor='hsla(0, 0%, 100%, 0.24)'
          borderColor='white'
          borderRadius='2px'
          color='white'
          icon={<MoreHorizontalIcon height='24px' width='24px' />}
          size='sm'
          variant='solid'
          zIndex={2}
        />
        <Portal>
          <MenuList fontSize='sm'>
            <FavoriteMenuItem programKey={programKey} programTitle={programTitle} />
            <MenuItem
              aria-label={t('program.removeEnrollmentAriaLabel', { programTitle })!}
              py='4px'
              onClick={handleRemoveEnrollment}
            >
              {t('program.removeEnrollment')}
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>
    </Box>
  );
}
